import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { usePocketBase } from '../contexts/PocketBaseContext';

const { FiDownload, FiHeadphones, FiEye, FiEdit } = FiIcons;

const BookCard = ({ book, onEdit }) => {
  const { user, isAdmin } = usePocketBase();

  const getCoverUrl = () => {
    if (book.cover_thumb_file) {
      return `https://leaf-surrounded-my-book-hub.pockethost.io/api/files/${book.collectionId}/${book.id}/${book.cover_thumb_file}`;
    }
    if (book.cover_source_url) {
      return book.cover_source_url;
    }
    return null;
  };

  const getPdfUrl = () => {
    if (!book.transcript_file) return null;
    return `https://leaf-surrounded-my-book-hub.pockethost.io/api/files/${book.collectionId}/${book.id}/${book.transcript_file}`;
  };

  const handleCardClick = (e) => {
    // If admin and shift+click, open edit modal
    if (isAdmin && e.shiftKey) {
      e.preventDefault();
      onEdit && onEdit(book);
    }
  };

  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onEdit && onEdit(book);
  };

  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group h-full flex flex-col relative"
      whileHover={{ y: -5, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Admin Edit Button */}
      {isAdmin && (
        <motion.button
          onClick={handleEditClick}
          className="absolute top-2 right-2 z-10 p-2 bg-indigo-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          title="Edit book (Shift+Click also works)"
        >
          <SafeIcon icon={FiEdit} className="text-sm" />
        </motion.button>
      )}

      <Link 
        to={`/book/${book.id}`} 
        className="flex flex-col h-full"
        onClick={handleCardClick}
      >
        {/* Cover Image - Fixed height */}
        <div className="relative h-64 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden flex-shrink-0">
          {getCoverUrl() ? (
            <img 
              src={getCoverUrl()} 
              alt={book.title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gradient-to-br from-indigo-100 to-purple-100">
              <SafeIcon icon={FiEye} className="text-4xl text-indigo-400" />
            </div>
          )}

          {/* Overlay with actions */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex space-x-2">
              {book.transcript_file && (
                <motion.a
                  href={getPdfUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-2 bg-white/90 rounded-full"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <SafeIcon icon={FiDownload} className="text-indigo-600" />
                </motion.a>
              )}
              {book.audiobook_url && (
                <motion.a
                  href={book.audiobook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-2 bg-white/90 rounded-full"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <SafeIcon icon={FiHeadphones} className="text-purple-600" />
                </motion.a>
              )}
            </div>
          </div>
        </div>

        {/* Content - Flexible height */}
        <div className="p-6 flex flex-col flex-grow">
          {/* Title - Fixed height with clamp */}
          <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors min-h-[3.5rem]">
            {book.title}
          </h3>

          {/* Author - Fixed height */}
          <p className="text-gray-600 mb-3 min-h-[1.5rem]">{book.author}</p>

          {/* Spacer to push metadata to bottom */}
          <div className="flex-grow"></div>

          {/* Metadata - Fixed height at bottom */}
          <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
            <div className="flex items-center space-x-2">
              {book.language && <span>{book.language}</span>}
            </div>
            {isAdmin && (
              <span className="text-xs text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
                Shift+Click to edit
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default BookCard;